/* Core & libraries */
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from 'react-jss';
import { defineMessages, useIntl } from 'react-intl';
import { Field } from 'formik';

/* Helpers, constants, custom methods, internal libraries */
/* Components */
import { ReactComponent as CloseIconSvg } from '../../../../resources/images/close-icon.svg';

/* Styles */
import styles from './styles.js';

const messages = defineMessages({
  textAreaMessage: {
    id: 'invitepeoplemodal.placeholder',
  },
});

const EmailTextarea = ({
  emails,
  className,
  setArrayHelpers,
  removeArrayHelpers,
  updateArrayHelpers,
  errors,
  describedByIds
}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const [inputValue, setInputValue] = useState('');
  const [inputWidth, setInputWidth] = useState(0);

  const [invalidEmails, setInvalidEmails] = useState([]);

  useEffect(() => {
    verifyEmails();
  }, [emails]);

  const verifyEmails = () => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmailsArray = [];

    emails.forEach((email) => {
      if (!email.match(emailPattern)) {
        invalidEmailsArray.push(email);
      }
    });
    setInvalidEmails(invalidEmailsArray);
  };

  const spanRefs = useRef({});
  const inputRef = useRef(null);

  const handleClickContainer = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleWidth = (id) => {
    const spanWidth = spanRefs.current[id]?.offsetWidth || 0;
    return { width: spanWidth + 5 };
  };

  useEffect(() => {
    const width = handleWidth(emails.length).width + 20;
    setInputWidth(width);
  }, [inputValue]);

  const handleChange = (e) => {
    const value = e.target.value.replace(/\s/g, ''); // Remove all spaces from the input
    setInputValue(value);
  };

  const addEmails = (emailsToAdd) => {
    emailsToAdd?.map((e) => {
      setArrayHelpers(e);
    });
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (['Enter', ' ', ','].includes(e.key)) {
      e.preventDefault();
      if (inputValue) {
        addEmails([inputValue]);
      }
    } else if (['Backspace', 'Delete'].includes(e.key) && inputValue === '') {
      e.preventDefault();
      if (emails.length > 0) {
        removeArrayHelpers(emails.length - 1);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData?.getData('text');
    if (pastedData) {
      const pastedEmails = pastedData.match(/[\w\d.+-]+@[\w\d.-]+\.[\w\d.-]+/g);
      addEmails(pastedEmails);
    }
  };

  const onBlurAdd = (e) => {
    if (inputValue) {
      addEmails([inputValue]);
    }
  };

  const updateState = (key, e) => {
    const newArray = [...emails];
    newArray[key] = e.target.value;

    if (newArray[key] === ' ' || newArray[key] === '') {
      removeArrayHelpers(key);
    } else {
      updateArrayHelpers(newArray);
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div
      className={`${classes.emailAddressesContainer} ${
        errors || invalidEmails.length > 0 ? classes.errorBorder : ''
      }`}
    >
      {emails?.map((item, id) => {
        return (
          <div key={id}>
            <Field
              name={`emailAddresses.${id}`}
              id={`emailAddresses.${id}`}
              render={({ field, form }) => (
                <div
                  className={
                    errors && invalidEmails.includes(item)
                      ? classes.errorTagItemContainer
                      : classes.tagItemContainer
                  }
                >
                  <span
                    key={id}
                    className={classes.span}
                    ref={(el) => (spanRefs.current[id] = el)}
                  >
                    {item}
                  </span>
                  <input
                    {...field}
                    {...form}
                    className={
                      errors && invalidEmails.includes(item)
                        ? classes.errorTagItem
                        : classes.tagItem
                    }
                    style={handleWidth(id)}
                    onChange={(e) => updateState(id, e)}
                    onKeyDown={(e) => {
                      if (['Enter', ' '].includes(e.key)) {
                        e.target.blur();
                        inputRef.current.focus();
                      }
                    }}
                    onFocus={handleFocus}
                    onBlur={onBlurAdd}
                    value={item}
                  />
                  <button
                    type='button'
                    className={classes.closeBtn}
                    onClick={(e) => {
                      removeArrayHelpers(id);
                      document.activeElement.blur();
                    }}
                    tabIndex={0}
                    aria-label={intl.formatMessage({id: "invitepeoplemodal.removeEmailAriaLabel"}, {email: item})}
                  >
                    <CloseIconSvg
                      className={`${
                        errors && invalidEmails.includes(item)
                          ? classes.errorCloseIcon
                          : classes.closeIcon
                      }`}
                    />
                  </button>
                </div>
              )}
            />
          </div>
        );
      })}
      <Field
        name={`emailAddresses`}
        id={`emailAddresses`}
        render={({ field, form }) => (
          <>
            <span
              className={classes.span}
              ref={(el) => (spanRefs.current[emails.length] = el)}
            >
              {inputValue}
            </span>
            {(emails?.length === 0 && !inputRef?.current?.value) && (
              <span
                className={classes.placeholderSpan}
                onClick={handleClickContainer}
              >
                {intl.formatMessage(messages.textAreaMessage)}
              </span>
            )}
            <input
              {...field}
              {...form}
              ref={inputRef}
              name='emailAddresses'
              className={`${classes.inputEmail} ${className ? className : ''}`}
              style={{
                width: inputWidth,
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onChange={handleChange}
              onBlur={onBlurAdd}
              value={inputValue}
              aria-describedby={describedByIds}
            />
          </>
        )}
      />
      <span onClick={handleClickContainer} className={classes.spanBox}></span>
    </div>
  );
};

export default EmailTextarea;
